import Button, { ButtonProps } from '@carvana/ds-button';
import { AnalyticsAttributes } from '@carvana/merch-components/contracts';
import cx from 'classnames';
import React, { useCallback } from 'react';
import PrimaryCtaType from '~/constants/PrimaryCtaType';
import useAccountModalReady from '~/hooks/common/accountModal/useAccountModalReady';
import { usePrimaryButtonHandler } from '~/hooks/vdp/cta';
import usePrimaryCtaButtonCopy from '~/hooks/vdp/selectors/usePrimaryCtaButtonCopy';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import useShowAccountModalPlaceholder from '~/hooks/vdp/useShowAccountModalPlaceholder';

const dummyFunction = () => {};

// Types that open the account modal for logged-out users and require it to be ready.
const MODAL_CTA_TYPES = new Set([
  PrimaryCtaType.GetStarted,
  PrimaryCtaType.Reservable
]);

type OwnProps<T> = {
  ButtonComponent?: React.ComponentType<any>;
  className?: string;
  'data-analytics-id': string | null;
  'data-analytics-attributes'?: AnalyticsAttributes;
  'data-testid'?: string;
  getStartedText?: string;
  onClick?: () => void;
} & T;

export default function PrimaryCtaButton<T = ButtonProps>({
  ButtonComponent = Button,
  className = '',
  'data-analytics-id': analyticsId,
  'data-analytics-attributes': analyticsAttributes,
  'data-testid': testId,
  getStartedText,
  onClick = dummyFunction,
  ...rest
}: OwnProps<T>) {
  const showPlaceholder = useShowAccountModalPlaceholder();
  const { whenReady, isPending: isPendingModal } = useAccountModalReady({
    showPlaceholder
  });
  const {
    isDisabled,
    isLoading: isAvailabilityLoading,
    type
  } = usePrimaryCtaButtonState();
  const rawClickHandler = usePrimaryButtonHandler(onClick) ?? dummyFunction;
  const primaryCtaText = usePrimaryCtaButtonCopy(getStartedText);

  const handleOnClick = useCallback(() => {
    if (MODAL_CTA_TYPES.has(type)) {
      whenReady(rawClickHandler);
    } else {
      rawClickHandler();
    }
  }, [whenReady, rawClickHandler, type]);

  const isLoading = isAvailabilityLoading || isPendingModal;

  return primaryCtaText ? (
    <ButtonComponent
      className={cx(className, {
        '!bg-[#00874a] hover:!bg-[var(--reference-green-600)] active:!bg-[#0a4729]':
          !isDisabled
      })}
      disabled={isDisabled}
      data-adobe-id={null}
      data-analytics-id={analyticsId}
      data-analytics-attributes={JSON.stringify({
        ...analyticsAttributes,
        description: 'primary cta',
        text: primaryCtaText
      })}
      data-testid={testId}
      isLoading={isLoading}
      onClick={handleOnClick}
      variant="primary"
      {...rest}
    >
      {primaryCtaText}
    </ButtonComponent>
  ) : null;
}
